import { MutationCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import i18n from '@/i18n/config';
import { toast } from '@/stores/toast';

/**
 * The app-wide QueryClient. Lives in its own module so the SAME instance is used
 * by the React provider (in ``__root``) AND the router context — that way route
 * ``loader`` prefetches populate the very cache the components read.
 */
export const queryClient = new QueryClient({
  // Treat data as fresh for 15s to avoid duplicate refetches on quick
  // navigation/remount; ``refetchOnWindowFocus`` stays on so returning to the
  // tab still pulls fresh data (matters for multi-user edits). Mutations still
  // invalidate their affected keys explicitly, so own changes show immediately.
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (failureCount, error) => {
        if (error.name === 'AbortError') return false;
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500 &&
          ![408, 425, 429].includes(error.status)
        )
          return false;
        return failureCount < 3;
      },
    },
  },
  // Surface every failed mutation as a toast (single global feedback point).
  // Opt out per-mutation with ``meta: { suppressErrorToast: true }`` when the
  // call site shows its own feedback (e.g. an inline message or bespoke toast).
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.meta?.suppressErrorToast) return;
      const message =
        error instanceof Error && error.message ? error.message : i18n.t('common.mutationError');
      toast.error(message);
    },
  }),
});
